import React from 'react';

import { FieldProps } from 'formik';
import styled, { css } from 'styled-components';

import IconCheck from 'components/icons/check';
import VisuallyHidden from 'components/visually-hidden';
import useIdCounter from 'hooks/use-id-counter';
import { FormValidationState } from 'utils/form';

import checkBackgroundColor from './check-background-color';
import Label from './label';
import LabelText from './label-text';

const HiddenInput = VisuallyHidden.withComponent('input');

interface IValidOrInvalid {
  valid: boolean;
  invalid: boolean;
}

interface ICheckable {
  checked: boolean;
}

// div cannot appear inside of p
const Message = styled.span<IValidOrInvalid>`
  display: block;
  font-size: 0.75rem;
  margin: 0;
  padding: 0;

  ${p => p.invalid && `color: ${Styles.color.error};`}
  ${p => p.valid && `color: ${Styles.color.validated};`}
`;

const baseSizeRem = 1.5;
const checkVerticalAdjustmentTop = baseSizeRem - 1.4;
const checkVerticalAdjustmentBottom = 0 - checkVerticalAdjustmentTop;

const checkCircleStyles = css`
  content: '';
  position: absolute;
  top: ${checkVerticalAdjustmentTop}rem;
  bottom: ${checkVerticalAdjustmentBottom}rem;
  left: 0;
  right: 0;
  border-radius: 5px;
  transition: all 200ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
`;

const Check = styled.span<IValidOrInvalid & ICheckable>`
  margin-top: -0.1rem;
  flex-grow: 0;
  flex-shrink: 0;
  position: relative;
  width: ${baseSizeRem}rem;
  height: ${baseSizeRem}rem;

  &::before {
    ${checkCircleStyles};

    ${p => p.invalid && `border-color: ${Styles.color.error};`}
    ${p => p.valid && `border-color: ${Styles.color.validated};`}
  }

  ${HiddenInput}:focus + ${Label} & {
    &::before {
      border: 2px solid rgba(0, 0, 0, 1);
    }
  }

  &::after {
    ${checkCircleStyles};

    ${props =>
      props.checked
        ? `
            background-color: ${checkBackgroundColor};
            transform: scale(0.75);
          `
        : `
            background-color: ${Styles.color.white};
            border: 1px solid rgba(0, 0, 0, 0.25);
            transform: scale(0.7);
    `};
  }

  ${HiddenInput}[disabled] + ${Label} & {
    cursor: not-allowed;
    &::after {
      opacity: 0.25;
    }
  }
`;

const CheckboxContainer = styled.div`
  position: relative;
`;

const CheckboxCheck = styled(IconCheck)`
  fill: ${Styles.color.white};
  height: 7px;
  margin: 0.55rem 0 0 0.45rem;
  position: absolute;
  width: 10px;
  z-index: ${Styles.zIndex.below};
`;

interface ICheckbox {
  'data-testid'?: string;
  checked: boolean;
  className?: string;
  disabled?: boolean;
  label: React.ReactNode;
  message?: string;
  name: string;
  onChange?: React.ReactEventHandler;
  required?: boolean;
  validation?: 'VALID' | 'INVALID';
}

export const Checkbox: React.FC<ICheckbox & ICheckable> = props => {
  const inputID = useIdCounter('checkbox');
  const messageID = useIdCounter('message');
  const invalid = props.validation === 'INVALID';
  const valid = props.validation === 'VALID';

  return (
    <>
      <HiddenInput
        aria-describedby={messageID}
        type="checkbox"
        id={inputID}
        name={props.name}
        checked={props.checked}
        onChange={props.onChange}
        disabled={props.disabled}
        data-testid={props['data-testid']}
        required={props.required}
      />
      <Label className={props.className} htmlFor={inputID} data-testid={props.name}>
        <CheckboxContainer>{props.checked && <CheckboxCheck />}</CheckboxContainer>
        <Check checked={props.checked} invalid={invalid} valid={valid} />
        <LabelText>
          {props.label}
          {props.message && (
            <Message invalid={invalid} valid={valid} id={messageID}>
              {props.message}
            </Message>
          )}
        </LabelText>
      </Label>
    </>
  );
};

export const FormikCheckbox: React.FC<FieldProps & ICheckbox & ICheckable> = ({
  form,
  field,
  ...props
}) => {
  const { name } = field;
  const checked = !!form.values[name];
  const validation =
    form.errors[name] && form.touched[name] ? FormValidationState.INVALID : undefined;
  const message =
    form.errors[name] && form.touched[name] ? (form.errors[name] as string) : undefined;
  return (
    <Checkbox {...field} {...props} checked={checked} validation={validation} message={message} />
  );
};

export default Checkbox;
